import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DocsCodeBlock as Container, DocsCodeToolbar } from '../Docs.styles';
import {
  makeSnippetId,
  snippetEditorUrl,
  stashSnippet
} from '../../../utils/docSnippet';

export interface DocsCodeBlockProps {
  code: string;
  /** The fence's language token, e.g. `js` or `js-runnable`. */
  language?: string;
  /** Sketch name to give the new project; the doc page title reads well here. */
  snippetTitle?: string;
}

/** A ` ```js-runnable ` fence gets an "Open in editor" button; plain fences get Copy only. */
export const isRunnableLanguage = (language?: string) =>
  !!language && language.endsWith('-runnable');

/**
 * A fenced code block with a Copy button and — for runnable fences — an "Open in editor" link
 * that opens the snippet as a new sketch in a new tab (see `utils/docSnippet`).
 *
 * The link's `href` is built at render so middle-click and cmd-click still work; the click
 * handlers only stash the code under the id already baked into that href.
 */
const DocsCodeBlock = ({
  code,
  language,
  snippetTitle
}: DocsCodeBlockProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const snippetId = useMemo(() => makeSnippetId(), []);
  const runnable = isRunnableLanguage(language);

  const handleCopy = () => {
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(done, () => {});
    }
  };

  const handleOpen = () => stashSnippet(snippetId, code, snippetTitle);

  return (
    <Container>
      <DocsCodeToolbar>
        <button type="button" onClick={handleCopy}>
          {copied
            ? t('Docs.Copied', { defaultValue: 'Copied' })
            : t('Docs.Copy', { defaultValue: 'Copy' })}
        </button>
        {runnable && (
          <a
            href={snippetEditorUrl(snippetId)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleOpen}
            onAuxClick={handleOpen}
          >
            {t('Docs.OpenInEditor', { defaultValue: 'Open in editor ↗' })}
          </a>
        )}
      </DocsCodeToolbar>
      <pre>
        <code>{code}</code>
      </pre>
    </Container>
  );
};

export default DocsCodeBlock;
